<template>
  <div class="nave-main d-flex align-items-stretch" :class="getClassTheme">
    <Toast />
    <template v-if="account">
      <side-menu-nave :sidebar="optionsMenu" />

      <main
        class="nave-main__main d-flex flex-column"
        :class="embeddedIframe ? '' : 'padding-bottom'"
      >
        <nav-menu-nave />

        <router-view v-slot="{ Component, route }">
          <transition name="fade-in-up">
            <section :key="route.name ? route.matched[0].name : route.name">
              <div
                :class="[embeddedIframe ? '' : ['nave-c-px-4', 'container']]"
              >
                <component :is="Component"></component>
              </div>
            </section>
          </transition>
        </router-view>
      </main>
    </template>
    <Toast class="nave-main__ws-main" position="bottom-right" group="ws">
      <template #message="slotProps">
        <p class="nave-main__ws-main__text mb-0">
          <i class="icon-nav-check" />
          {{ slotProps.message.detail }}
          <i class="icon-nav-close" @click="closeToastGroup('ws')" />
        </p>
      </template>
    </Toast>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, computed } from "vue";
import { useToast } from "primevue/usetoast";
import { useStore } from "vuex";
import { HttpAxios } from "@/common/services/httpAxios";
import { useRoute } from "vue-router";
import Monitoring from "@/common/services/monitoring";
import AclModule from "@/common/module/acl";
import navMenuNave from "@/components/atomic/organisms/navMenuNave/index.vue";
import sideMenuNave from "@/components/atomic/organisms/sideMenuNave.vue";
import mixpanel from "mixpanel-browser";
import KeyCloak from "@/common/module/keyCloak";

import { getSidebarItems } from "@/common/hooks/useSidebarItems";

declare const FB: any;

export default defineComponent({
  name: "App",

  components: {
    navMenuNave,
    sideMenuNave,
  },

  setup() {
    const store = useStore();
    const route = useRoute();
    const { sidebar, sidebarBemax }: any = getSidebarItems(
      AclModule.featureFlag("ENV_NOT_PROD")
    );

    const account = localStorage.getItem("ACCOUNT");
    const accountObject = JSON.parse(account ? account : "{}");

    const toast = useToast();
    let wsAudiences: null | WebSocket = null;

    const embeddedIframe = computed(() => {
      if (route.meta.iframe) return true;

      return false;
    });

    const getClassTheme = computed(() => {
      return store.getters["theme/active"];
    });

    const getAlerts = async () => {
      try {
        const alerts = await Monitoring.getAllAlerts();

        store.dispatch("alerts/SET_ALERTS", alerts.response.alerts);
      } catch (err) {
        console.log(err);
      }
    };
    /* ADD GTM SCRIPT IN HTML HEAD */
    const addGtm = () => {
      const gtmScript = document.createElement("script");
      gtmScript.setAttribute("src", process.env.VUE_APP_GTM as string);
      gtmScript.setAttribute("defer", "true");
      gtmScript.setAttribute("async", "true");
      document.head.appendChild(gtmScript);
    };

    const addGtag = (...values: any[]) => {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(values);
      (window as any).dataLayer.push(values[1]);
    };

    const closeToastGroup = toast.removeGroup;

    const connectWebsocketAudience = () => {
      wsAudiences = new WebSocket(
        `${
          HttpAxios.connectionWS
        }/notifications?authorization=${encodeURIComponent(
          HttpAxios.tokenBff
        )}&x-dxp-account-id=${accountObject.account ?? ""}`
      );
      wsAudiences.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const value = JSON.parse(data.data.$value);
        toast.add({
          detail: `Sua audiência "${value.msg}" está ${value.status}.`,
          life: 5000,
          group: "ws",
          styleClass: "nave-main__ws-msg",
          closable: false,
        });
      };
      wsAudiences.onclose = () => {
        console.warn("Websocket is closed!");
        wsAudiences = null;
        if (KeyCloak.validateAccessToken()) {
          setTimeout(connectWebsocketAudience, 1000);
        }
      };
    };

    const loadSdk = () => {
      (window as any).fbAsyncInit = function () {
        const FACEBOOK_APP_ID = process.env.VUE_APP_FACEBOOK_APP_ID;
        // JavaScript SDK configuration and setup
        FB.init({
          appId: FACEBOOK_APP_ID, // Facebook App ID
          cookie: true, // enable cookies
          xfbml: true, // parse social plugins on this page
          version: "v14.0", //Graph API version
          logging: true, //
        });
      };

      // Load the JavaScript SDK asynchronously
      (function (d: any, s: any, id: any) {
        var js,
          fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        fjs.parentNode.insertBefore(js, fjs);
      })(document, "script", "facebook-jssdk");
    };

    const optionsMenu = computed(() => {
      return route.meta.templateSidebar == "sidebarBemax"
        ? sidebarBemax
        : sidebar;
    });

    onMounted(() => {
      if (!account) {
        toast.add({
          severity: "error",
          summary: "Você não possui uma conta vinculada.",
          detail: "Para continuar, entre em contato com seu administrador.",
          life: 3000,
        });

        setTimeout(() => {
          KeyCloak.redirectAuth();
        }, 3000);
      } else {
        if (process.env.VUE_APP_GTM) {
          addGtag("set", {
            user_customer_id: accountObject.code.customerId,
            user_id_login: store.getters["user/user"]._id,
          });
          addGtm();
        }
      }

      getAlerts();

      mixpanel.track("login", {
        plataforma: "Wake Experience",
        sucesso: true,
      });

      try {
        connectWebsocketAudience();
      } catch (error) {
        console.log("connectWebsocketAudience:", error);
      }

      try {
        loadSdk();
      } catch (error) {
        console.log("load sdk facebook:", error);
      }
    });

    return {
      embeddedIframe,
      sidebar,
      sidebarBemax,
      account,
      getClassTheme,
      addGtm,
      addGtag,
      closeToastGroup,
      optionsMenu,
    };
  },
});
</script>

<style lang="scss" scoped>
.nave-main {
  min-height: 100vh;

  &__main {
    position: relative;
    flex: 1 0 auto;
    background-color: #f6f6f6;
    transition: all 2s ease;
    &::before {
      position: fixed;
      content: "";
      height: 45vh;
      width: 100%;
      background: linear-gradient(180deg, transparent 0%, transparent 100%);
      z-index: -2;
    }

    &.padding-bottom {
      padding-bottom: toRem(100px);
    }
  }
}
</style>
<style lang="scss">
.nave-main {
  &__ws-main {
    &.p-toast {
      width: fit-content;
      white-space: nowrap;
      opacity: 1;

      .p-toast-message {
        border-radius: 6px;
      }
    }

    &__text {
      font-family: "text-semi-bold";
      font-size: toRem(18px);

      > i {
        position: relative;

        &:first-child {
          top: 4px;
          font-size: toRem(22px);
        }

        &:last-child {
          top: 2px;
          cursor: pointer;
          font-size: toRem(12px);
          color: var(--insights-color-16);
          margin-left: toRem(15px);
        }
      }
    }
  }
  &__ws-msg {
    background-color: var(--insights-color-1);
    color: var(--insights-color-2);
  }
}
</style>
